import { type ReactNode, useCallback, useRef } from 'react';

import IconButton from 'components/IconButton';
import ModalDialog, {
	MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS,
} from 'components/ModalDialog';
import Text from 'components/Text';
import { useDebounce, useId, useValueChangeEffect } from 'hooks';
import { cn, cnm } from 'utils/classNames';
import { selectFirstFocusable } from 'utils/dom';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import PopoverContextProvider, {
	usePopoverContext,
} from './PopoverContextProvider';

export type PopoverVariant = 'sidePanel' | 'sidePanelWide';

interface ExtraPageParts {
	/** Content after the heading and close button for this page. */
	afterHeader?: ReactNode;

	/** Content before the heading and close button for this page. */
	beforeHeader?: ReactNode;

	/** Extra class names for the content container. */
	contentClassName?: string;

	/** Content after the main content for this page. */
	footer?: ReactNode;

	/** If the `beforeHeader` content should have the content padding. */
	padAfterHeader?: boolean;

	/** If the `afterHeader` content should have the content padding. */
	padBeforeHeader?: boolean;

	/** If the main content should have padding. */
	padContent?: boolean;

	/** If the `footer` content should have the content padding. */
	padFooter?: boolean;
}

interface BaseProps extends ExtraPageParts {
	/** ID for integration testing. */
	dataCy?: string;

	/** The popover container ID. */
	id?: string;

	/** True if the Popover should be open. */
	isOpen: boolean;

	/** Callback function when the popover has been closed and the transition is done. */
	onCloseDone?: () => void;

	/** Callback function when the popover is opened. */
	onOpen?: () => void;

	/**
	 * Where to look for a focus target when the popover (or a new page in it)
	 * is opened.
	 *
	 * dialog: The first focusable in the popover root (usually the close button).
	 * content: The first focusable in the main popover content.
	 */
	openingFocusTargetRoot?: 'content' | 'dialog';

	/** Custom ID for the title element, is otherwise generated. */
	titleId?: string;

	/** The style of popover to use. */
	variant?: PopoverVariant;
}

interface PopoverPage extends ExtraPageParts {
	/** Custom text for the back button on this page. */
	backButtonText?: string;

	/** Main content for this page. */
	content: ReactNode;

	/** Hide the title for this page? Avoid where possible, visible is better for accessibility. */
	hiddenTitle?: boolean;

	/** A unique page ID. */
	id?: string;

	/** Modal title for this page. */
	title: string;
}

interface PropsWithChildren extends BaseProps {
	backButtonText?: never;

	/** Main content. */
	children: ReactNode;

	currentPageIndex?: never;

	/** Hide the title? Avoid where possible, visible is better for accessibility. */
	hiddenTitle?: boolean;
	onBackClick?: never;
	pages?: never;

	/** The modal title. */
	title: string;
}

interface PropsWithPages extends BaseProps {
	/** Custom text for the back button. */
	backButtonText?: string;

	children?: never;

	/** The currently active page to display. */
	currentPageIndex: number;

	/** Back button click handler. */
	onBackClick?: () => void;

	/** Pages when a popover contains multiple steps. */
	pages: (false | PopoverPage | null | undefined)[];

	title?: never;
}

type PopoverWithoutContextProps = PropsWithChildren | PropsWithPages;

/** Dialog that slides in from the edge. */
function PopoverWithoutContext(props: PopoverWithoutContextProps) {
	const { t } = useI18n();
	const contentRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const {
		currentPageIndex = 0,
		dataCy,
		id: idProp,
		isOpen,
		onBackClick,
		onCloseDone,
		onOpen,
		openingFocusTargetRoot = 'dialog',
		titleId,
		variant = 'sidePanel',
	} = props;

	const { onClose, shouldCloseOnOutsideClick } = usePopoverContext();

	const fallbackId = useId('popover');
	const id = idProp || fallbackId;
	const headingId = titleId || `${id}-heading`;

	const basePageData = {
		afterHeader: props.afterHeader,
		beforeHeader: props.beforeHeader,
		contentClassName: props.contentClassName,
		footer: props.footer,
		padAfterHeader: props.padAfterHeader,
		padBeforeHeader: props.padBeforeHeader,
		padContent: props.padContent,
		padFooter: props.padFooter,
	} as const;

	const pages: PopoverPage[] = props.pages
		? props.pages.filter(is.truthy).map((page, i) => ({
				// Use the main props as default values, e.g. to have the same footer
				// on all pages.
				...basePageData,
				id: `${id}-page-${i}`,
				...page,
			}))
		: [
				{
					...basePageData,
					id,
					content: props.children,
					title: props.title,
					hiddenTitle: props.hiddenTitle,
				},
			];

	const currentPage = pages[currentPageIndex] ?? pages[0]!;
	const {
		afterHeader,
		beforeHeader,
		content,
		contentClassName,
		footer,
		hiddenTitle,
		padAfterHeader,
		padBeforeHeader,
		padContent = true,
		padFooter,
		title,
	} = currentPage;

	// Only check if props.pages is set, it may only have a length of one due
	// to conditionals.
	const hasBackButton = Boolean(
		props.pages && currentPageIndex > 0 && onBackClick,
	);
	const backButtonRef = useRef<HTMLButtonElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	const transitionDuration = 200;
	const transitionDurationClass = 'duration-200';

	const onDialogClose = useCallback(() => {
		onClose();
		if (onCloseDone) {
			setTimeout(() => {
				onCloseDone();
			}, transitionDuration);
		}
	}, [onClose, onCloseDone]);

	// Move focus to the close button when the back button disappears while it
	// has focus.
	useValueChangeEffect(hasBackButton, (prevHasBackButton) => {
		if (
			prevHasBackButton &&
			!hasBackButton &&
			backButtonRef.current &&
			document.activeElement === backButtonRef.current
		) {
			closeButtonRef.current?.focus();
		}
	});

	// Handle focus when changing pages.
	useValueChangeEffect(currentPageIndex, () => {
		// Is there an element in the dialog that has focus? If so, let it keep it.
		if (
			dialogRef.current &&
			!dialogRef.current.contains(document.activeElement)
		) {
			return;
		}
		// Let the back button start its transition before trying to focus,
		// otherwise focusing can fail due to visibility hidden.
		setTimeout(() => {
			if (!contentRef.current || !dialogRef.current) {
				return;
			}
			const target =
				openingFocusTargetRoot === 'content'
					? (selectFirstFocusable(contentRef.current) ??
						selectFirstFocusable(dialogRef.current))
					: selectFirstFocusable(dialogRef.current);
			target?.focus();
		}, transitionDuration / 2);
	});

	const isOpenDebounced = useDebounce(isOpen, transitionDuration);
	const hasContent = isOpen || isOpenDebounced;

	return (
		<ModalDialog
			id={id}
			ref={dialogRef}
			isOpen={isOpen}
			ariaLabelledby={headingId}
			onClose={onDialogClose}
			onOpen={onOpen}
			shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
			scrollLockRef={contentRef}
			className={cn(
				'bg-base shadow-center outline-0 max-sm:rounded-t-md',
				'sm:forced-colors:border-l',
				'w-full',
				isOpen && 'flex flex-col',
				'max-sm:mt-auto max-sm:h-[calc(100%-4rem)] sm:ml-auto sm:h-full',
				variant === 'sidePanel' && 'sm:w-120',
				variant === 'sidePanelWide' && 'sm:w-240',
				'transition-[opacity,translate] transition-discrete',
				transitionDurationClass,
				'opacity-100 starting:opacity-0',
				'max-sm:translate-y-0 max-sm:starting:translate-y-[5%]',
				'sm:translate-x-0 sm:starting:translate-x-[10%]',
			)}
			dataCy={dataCy}
		>
			{hasContent &&
				beforeHeader &&
				(padBeforeHeader ? (
					<div className="border-b border-b-strong p-r-md">{beforeHeader}</div>
				) : (
					beforeHeader
				))}

			<div className="relative flex min-h-14 shrink-0 items-center px-4 py-1 md:min-h-16 md:px-6">
				{onBackClick && (
					<IconButton
						ref={backButtonRef}
						icon="chevron-left"
						variant="borderless"
						text={
							currentPage.backButtonText ||
							props.backButtonText ||
							t('general_back_button_text')
						}
						textDisplay="trailing"
						textClassName="text-sm"
						className={cn(
							'absolute top-1/2 left-4 -translate-y-1/2 md:left-6',
							// Visually align icon edge
							'-ml-4 pl-0',
							// Transition triggers on render, avoid having the button
							// slide in when the popover opens.
							isOpenDebounced && [
								'transition-[visibility,opacity,translate]',
								transitionDurationClass,
							],
							!hasBackButton && 'invisible translate-x-3 opacity-0',
						)}
						onClick={onBackClick}
					/>
				)}

				<IconButton
					ref={closeButtonRef}
					icon="close"
					variant="borderless"
					text={t('general_close_dialog_button_label')}
					// Visually align icon edge
					className="-mr-4 ml-auto"
					onClick={() => {
						onDialogClose();
					}}
				/>
			</div>

			{hasContent &&
				afterHeader &&
				(padAfterHeader ? (
					<div className="border-y border-y-strong p-4 md:p-6">
						{afterHeader}
					</div>
				) : (
					afterHeader
				))}

			<div
				ref={contentRef}
				className={cnm(
					'grow overflow-y-auto overscroll-contain',
					MODAL_DIALOG_TOUCH_MOVE_WHITELIST_CLASS,
					padContent && 'px-r-md pb-r-md',
					contentClassName,
					afterHeader && 'pt-r-md',
				)}
			>
				<Text
					as="h2"
					className={cn('mb-4', hiddenTitle && 'sr-only')}
					id={headingId}
				>
					{title}
				</Text>

				{hasContent && content}
			</div>

			{hasContent &&
				footer &&
				(padFooter ? (
					<div className="border-t border-t-strong p-r-md">{footer}</div>
				) : (
					footer
				))}
		</ModalDialog>
	);
}
PopoverWithoutContext.displayName = 'PopoverWithoutContext';

type Props = {
	/** Callback function when the popover should close. */
	onClose: () => void;

	/** If the popover should close when clicking on the background outside it. */
	shouldCloseOnOutsideClick?: boolean;
} & PopoverWithoutContextProps;

export default function Popover({
	onClose,
	shouldCloseOnOutsideClick,
	...props
}: Props) {
	return (
		<PopoverContextProvider
			shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
			onClose={onClose}
		>
			<PopoverWithoutContext {...props} />
		</PopoverContextProvider>
	);
}

Popover.displayName = 'Popover';
